import { Component, OnDestroy, OnInit } from '@angular/core';
import { WebsiteBuilderService } from '../../../services/website-builder.service';
import { Subscription } from 'rxjs/Subscription';
import { WebsiteBuilderView } from '../../../types/website-builder/website-builder-view';
import { IAnnouncement } from '../../../types/website-builder/announcement';
import { AnnouncementApiService } from '../../../services/api/announcement-api.service';
import { WebsitePage } from '../../../types/website-builder/website-page';
import { WebsiteBuilderItemAmenities, WebsiteBuilderItemNeighbourhood } from '../../../types/website-builder/items';
import { IPublicAmenity } from '../../../types/api-response/amenity/public-amenity';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

declare const $: any;

@Component({
    selector: 'website-builder-view',
    templateUrl: './website-builder-view.component.html'
})

export class WebsiteBuilderViewComponent implements OnInit, OnDestroy {
    map: any;
    navPosition = 'home';
    iframeWindow: Window;
    viewDocument$ = new Subscription;
    showMobileMenu: boolean = false;
    websiteBuilderView = WebsiteBuilderView;
    websitePage = WebsitePage;

    private loadedNotices = {};
    private loadPromises = {};

    constructor(
        public websiteBuilderService: WebsiteBuilderService,
        private announcementApi: AnnouncementApiService,
        private sanitizer: DomSanitizer
    ) {}

    ngOnInit() {
        this.iframeWindow = this.websiteBuilderService.frameWindow;
        this.websiteBuilderService.onAddressChange.subscribe(() => {
            const item: WebsiteBuilderItemNeighbourhood = this.websiteBuilderService.data.data.websitePages[WebsitePage.neighbourhood] as WebsiteBuilderItemNeighbourhood;
            this.setStreetView(false, item, item.streetViewEnabled);
        });
        this.websiteBuilderService.toggleStreetView.subscribe((value) => {
            this.toggleStreetView(value);
        });

        const self = this;
        $(this.iframeWindow).bind('scroll', function ($event) {
            self.onBodyScroll();
        });
    }

    get amenities(): IPublicAmenity[] {
        return this.websiteBuilderService.data.data.publicAmenities.filter(amenity => {
            const amenityIds = (this.websiteBuilderService.data.data.websitePages[WebsitePage.amenities] as WebsiteBuilderItemAmenities).amenitiesIds;
            return !amenityIds.length || amenityIds.includes(amenity.id);
        });
    }

    getAmenityIcoStyle(amenity: IPublicAmenity) {
        const style = {};

        if (!amenity.amenityImage) {
            return style;
        }

        if(amenity.amenityImage.slice(-3) === 'svg') {
            style['-webkit-mask-image'] = `url(${amenity.amenityImage})`;
            style['background-color'] = amenity.amenityColor;
        } else {
            style['background-image'] = `url(${amenity.amenityImage})`;
        }

        return style;
    }

    ngOnDestroy() {
        this.viewDocument$.unsubscribe();
    }

    get selectedNotice(): IAnnouncement & {safeText: SafeHtml}|null {
        return this.loadNotice(this.websiteBuilderService.data.data.selectedNoticeId) as any;
    }

    loadNotice(noticeId): IAnnouncement|null {
        if(!noticeId) {
            return null;
        }

        if(!this.loadPromises[noticeId]) {
            this.loadPromises[noticeId] = this.announcementApi.getNotice(noticeId).toPromise();
            this.loadedNotices[noticeId] = null;
            this.loadPromises[noticeId].then(notice => {
                notice.safeText = this.sanitizer.bypassSecurityTrustHtml(notice.text);
                this.loadedNotices[noticeId] = notice;
            });
        }

        return this.loadedNotices[noticeId];
    }

    toggleMobileMenu(): void {
        this.showMobileMenu = !this.showMobileMenu;
    }

    goToLink(link: WebsiteBuilderView): void {
        this.websiteBuilderService.data.data.setCurrentMenu(link);

        if ([WebsiteBuilderView.documents, WebsiteBuilderView.announcements, WebsiteBuilderView.buildingFeatures].includes(link)) {
            this.websiteBuilderService.data.data.webView = link;
        } else {
            this.websiteBuilderService.data.data.webView = WebsiteBuilderView.index;
        }

        const $target =  this.iframeWindow.document.getElementById(link);

        if ($target) {
            this.iframeWindow.scrollTo(0, $target.offsetTop - 50);
        }

        this.showMobileMenu = false;
    }

    learnMoreClick() {
        for(const pageKey of this.websiteBuilderService.data.data.pageKeys) {
            if(pageKey === WebsitePage.welcome) {
                continue;
            }

            const page = this.websiteBuilderService.data.data.websitePages[pageKey];
            if(page.isPresent) {
                this.goToLink(page.navLink);
                break;
            }
        }
    }

    onMapReady(map, item): void {
        item.changedMap = map;
        if(item.streetViewEnabled)
            this.setStreetView(true, item, item.streetViewEnabled);
    }

    setStreetView(firstLoad, item: WebsiteBuilderItemNeighbourhood, visible): void {
        const astorPlace = {lat: item[firstLoad ? 'lat' : 'changedLat'], lng: item[firstLoad ? 'lng' : 'changedLng']};
        const panorama = item.changedMap.getStreetView();
        panorama.setPosition(astorPlace);
        panorama.setPov(({
            heading: item.map.pov ? item.map.pov.heading : 265,
            pitch: item.map.pov ? item.map.pov.pitch : 0,
            zoom: item.map.pov ? item.map.pov.zoom : 8
        }));
        panorama.setVisible(visible);
        panorama.setOptions({
            disableDefaultUI: true,
            // zoomControl: true
        });
    }

    toggleStreetView(value): void {
        const item = this.websiteBuilderService.data.data.websitePages[WebsitePage.neighbourhood] as WebsiteBuilderItemNeighbourhood;
        this.setStreetView(false, item, value);
    }

    onMapCenterChange(event, item): void {
       item.changedLat = event.lat;
       item.changedLng = event.lng;
    }

    resize($event, item) {
        window.dispatchEvent(new Event('resize'));
    }


    toggleCurrentMenu(): void {
        const iframeDocument = $(this.iframeWindow.document);
        const window_top = $(this.iframeWindow).scrollTop();
        for (const part of this.websiteBuilderService.data.data.websiteParts) {
            const elBlock = iframeDocument.find('#' + part.navLink);
            if (elBlock.length) {
                const div_top = elBlock.offset().top;
                const link = iframeDocument.find('[data-href="' + part.navLink + '"]');

                if (link.length && (window_top > div_top - 400)) {
                    this.websiteBuilderService.data.data.setCurrentMenu(part.navLink);
                }
            }
        }
    }

    toggleFixHeader(): void {
        if (this.iframeWindow.pageYOffset > 40) {
            $(this.iframeWindow.document).find('#header').addClass('header-fix');
        } else {
            $(this.iframeWindow.document).find('#header').removeClass('header-fix');
        }
    }

    onBodyScroll(): void {
       this.toggleFixHeader();
       this.toggleCurrentMenu();
    }
}



// WEBPACK FOOTER //
// /app/src/app/views/website-builder/website-builder-view/website-builder-view.component.ts